import { Alert, AlertTitle, Box, LinearProgress, Stack, Typography } from '@mui/material';
import { FC, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AutocompleteInput,
  BooleanInput,
  DeleteButton,
  Edit,
  EditProps,
  FormDataConsumer,
  Identifier,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useGetList,
  useRecordContext,
  useSaveContext,
  useTranslate,
  ValidationError,
  ValidationErrorMessage,
  ValidationErrorMessageWithArgs,
} from 'react-admin';
import { FieldValues, useFormContext } from 'react-hook-form';
import { SoftwareComponent, SoftwareRelease } from 'types';
import { SoftwareVersionInput } from './SoftwareVersionInput';

/**
 * Namespace the per-component version selects write to.
 *
 * `SoftwareReleaseCreate` lets every select write to the component's own name (`tover.tafel`),
 * which react-hook-form reads as a path and nests under a `tover` object — hence the
 * `tover[name.split('.')[1]]` in its toolbar. The edit form deliberately does *not* adopt that:
 * it keys by component **id** instead, so the namespace cannot collide, does not depend on
 * every component being named `<something>.<something>`, and — the reason that matters here —
 * can be rewritten wholesale in one `setValue` when the hardware changes.
 */
const COMPONENT_VERSIONS = 'component_versions';

/** Key of one component inside that namespace — `c` prefixed so it is never read as an index. */
const versionKey = (component: { id: number }): string => `c${component.id}`;

const versionFieldName = (component: { id: number }): string => `${COMPONENT_VERSIONS}.${versionKey(component)}`;

/**
 * The fields this form may send, an allow-list rather than a deny-list: it is exactly what
 * `UpdateRequest::rules()` validates, so a key the API resource grows later cannot start being
 * echoed back by accident. `software_version_ids` echoed verbatim is precisely what makes a
 * hardware change fail validation against the *new* hardware. **An input added to this form must
 * be added here, or its value is silently not saved.**
 */
const SUBMITTED_FIELDS = ['name', 'hardware_id', 'hardware_revision_id', 'custom'];

/** react-admin's transport format for an error that carries interpolation arguments. */
const VALIDATION_ERROR_PREFIX = '@@react-admin@@';

/** Reads the currently registered field names at the moment it is called — see `MountedFieldsProbe`. */
type MountedFieldsReader = (() => Set<string>) | undefined;

type ComponentVersions = Record<string, number | null | undefined>;

/**
 * Reads one entry of a 422 body as something react-admin can render.
 *
 * Since TVR-1915 `src/providers/data/index.ts` already hands back one **string** per field — a
 * plain message, or react-admin's `@@react-admin@@{"message","args"}` transport format when the
 * API attached interpolation arguments. Both pass through here untouched, and `<ValidationError>`
 * decodes the second form when it renders. The object branch below is kept because it costs a
 * handful of lines and is the only thing standing between an unreviewed provider change and a
 * silently blank error.
 */
const serverError = (value: unknown): ValidationErrorMessage | undefined => {
  if (typeof value === 'string') {
    return value.length > 0 ? value : undefined;
  }

  /** Defensive: the provider hands back one entry per field, but Laravel itself sends a list. */
  if (Array.isArray(value)) {
    return value.map(serverError).find((error) => error != null);
  }

  if (value == null || typeof value !== 'object' || !('message' in value)) {
    return undefined;
  }

  const { message, args } = value as { message: unknown; args?: unknown };
  if (typeof message !== 'string' || message.length === 0) {
    return undefined;
  }

  return args == null ? message : { message, args: args as ValidationErrorMessageWithArgs['args'] };
};

/**
 * react-hook-form stores an error message as a plain string, so an error with args travels in
 * react-admin's own escape hatch for exactly this — the format `useInput` emits for its
 * validators, and `ValidationError` parses back before translating.
 */
const toFieldError = (error: ValidationErrorMessage): string =>
  typeof error === 'string' ? error : `${VALIDATION_ERROR_PREFIX}${JSON.stringify(error)}`;

/**
 * Splits the server's field errors into the ones an input can show (returned to react-admin, which
 * sets them on the input) and the ones that belong to no input — `software_version_ids` above all —
 * which are shown in an alert instead of disappearing.
 *
 * Which fields have an input is asked of react-hook-form's registry of mounted fields rather than
 * kept in a list here: a list would have to be edited in step with the JSX below, and nothing —
 * not the compiler, not a test — would notice if it were not.
 */
const partitionServerErrors = (
  raw: unknown,
  hasInput: (field: string) => boolean,
): { fieldErrors: Record<string, string>; formErrors: ValidationErrorMessage[] } => {
  const fieldErrors: Record<string, string> = {};
  const formErrors: ValidationErrorMessage[] = [];

  if (raw == null || typeof raw !== 'object') {
    return { fieldErrors, formErrors };
  }

  Object.entries(raw as Record<string, unknown>).forEach(([field, value]) => {
    const error = serverError(value);
    if (error == null) {
      return;
    }

    if (hasInput(field)) {
      fieldErrors[field] = toFieldError(error);
    } else {
      formErrors.push(error);
    }
  });

  return { fieldErrors, formErrors };
};

/**
 * The selection the record already carries, keyed the way the form keys it.
 *
 * An entry can arrive without an `id`: the API builds these from the release's *versions*, so a
 * version whose component row is gone yields `{ software_version_id }` and nothing else. Such an
 * entry has no component row to be seeded into — see `ReleaseSoftwareComponent` in `types/`.
 */
const seedComponentVersions = (record?: SoftwareRelease): ComponentVersions =>
  (record?.software_components ?? []).reduce<ComponentVersions>((versions, component) => {
    if (component.id != null && component.software_version_id != null) {
      versions[versionKey({ id: component.id })] = component.software_version_id;
    }
    return versions;
  }, {});

/** Whether the release being edited already links any version at all. */
const releaseHasVersions = (record?: SoftwareRelease): boolean =>
  (record?.software_components ?? []).some((component) => component.software_version_id != null);

/**
 * Assembles the payload from the components of the hardware the form describes *now*, so a
 * component that does not exist for the newly picked hardware cannot be submitted.
 *
 * `software_components` is left out entirely when nothing is selected and the release carried no
 * versions to begin with: the API back-fills the release's own versions when the key is absent and
 * skips the rule when there are none, which is what keeps a version-less release renameable. Sending
 * an empty list instead would fail the moment the hardware has a required component.
 */
const buildPayload = (
  values: FieldValues,
  components: SoftwareComponent[],
  record?: SoftwareRelease,
): Record<string, unknown> => {
  const payload: Record<string, unknown> = Object.fromEntries(
    SUBMITTED_FIELDS.filter((field) => field in values).map((field) => [field, values[field]]),
  );

  const selected = (values[COMPONENT_VERSIONS] ?? {}) as ComponentVersions;
  const selection = components.map((component) => ({
    ...component,
    software_version_id: selected[versionKey(component)] ?? null,
  }));

  const hasSelection = selection.some((component) => component.software_version_id != null);

  if (hasSelection || releaseHasVersions(record)) {
    payload.software_components = selection;
  }

  return payload;
};

/**
 * Hardware picker that drops everything belonging to the hardware being left behind.
 *
 * Both resets are required by the API: it validates the submitted versions against the new
 * hardware, and applies a revision-scoped `exists` as soon as the hardware pair changes, so a
 * carried-over revision is a 422 of its own.
 *
 * The version namespace is overwritten with explicit `null`s rather than emptied. A component
 * that exists for both hardwares keeps its input mounted across the change, and react-hook-form
 * re-reads such a field as `get(formValues, name, get(defaultValues, name))` — an *absent* value
 * is restored from the seeded record selection, putting the old hardware's version straight back
 * into the payload. A `null` is a value, so nothing is restored.
 *
 * Coming back to the hardware the release is stored with restores what it was stored with, so a
 * hardware change the API refuses (a handed-out release) can be undone without re-picking every
 * version by hand.
 */
const HardwareInput = ({
  hardwareId,
  seededVersions,
  clearedVersions,
  onHardwareChange,
}: {
  hardwareId?: number;
  seededVersions: ComponentVersions;
  clearedVersions: ComponentVersions;
  onHardwareChange: (hardwareId?: number) => void;
}) => {
  const record = useRecordContext<SoftwareRelease>();
  const { setValue } = useFormContext();

  const handleChange = (value: unknown) => {
    /**
     * react-admin passes the choice's own id, typed `Identifier = string | number`, and `''` when
     * the input is cleared. Reading anything non-numeric as "cleared" would disable the form with
     * no message, so only an actually empty value counts as cleared.
     */
    const parsed = value === '' || value == null ? undefined : Number(value);
    const nextHardwareId = parsed != null && Number.isNaN(parsed) ? undefined : parsed;
    if (nextHardwareId === hardwareId) {
      return;
    }

    const isRecordHardware = nextHardwareId === record?.hardware_id;

    setValue(COMPONENT_VERSIONS, isRecordHardware ? seededVersions : clearedVersions, { shouldDirty: true });
    setValue('hardware_revision_id', isRecordHardware ? (record?.hardware_revision_id ?? null) : null, {
      shouldDirty: true,
    });
    onHardwareChange(nextHardwareId);
  };

  return (
    <ReferenceInput source="hardware_id" reference="hardware">
      <AutocompleteInput optionText="name" fullWidth validate={required()} onChange={handleChange} />
    </ReferenceInput>
  );
};

/**
 * One version select per component of the selected hardware — the list the API validates against,
 * not the list the record was saved with.
 */
const SoftwareComponentInputs = ({
  components,
  hardwareId,
  isLoading,
  hasError,
  enforceRequired,
}: {
  components?: SoftwareComponent[];
  hardwareId?: number;
  isLoading: boolean;
  hasError: boolean;
  enforceRequired: boolean;
}) => {
  const translate = useTranslate();

  if (hardwareId == null) {
    return null;
  }

  if (hasError) {
    return (
      <Alert severity="error" sx={{ width: '100%' }}>
        {translate('resources.software_releases.edit.components_error')}
      </Alert>
    );
  }

  if (isLoading) {
    return <LinearProgress sx={{ width: '100%' }} />;
  }

  if (components == null || components.length === 0) {
    return <Typography variant="body2">{translate('resources.software_releases.edit.no_components')}</Typography>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack>
        {components.map((component) => (
          /*
           * The label is a fragment, not the bare name: react-admin translates a string label, and
           * `tover.tafel` is a valid polyglot key path — a missing-key lookup on every render.
           */
          <SoftwareVersionInput
            key={component.id}
            component={component}
            source={versionFieldName(component)}
            label={<>{component.name}</>}
            enforceRequired={enforceRequired}
          />
        ))}
      </Stack>
    </Box>
  );
};

/**
 * Publishes react-hook-form's set of mounted field names to the submit handler, which runs outside
 * the form provider and so has no way to ask for it. Renders nothing.
 *
 * It publishes a *getter*, never the Set: `reset()` replaces `_names` wholesale, and `ra-core`'s
 * `useAugmentedForm` resets from an effect in the parent `Form` — which runs after this child's
 * effect. A captured Set would therefore be the pre-reset one, holding only the fields registered
 * in the first commit, and every version select (registered once the components query resolves)
 * would look like a field with no input. `control` itself survives the reset, so the read has to
 * happen when the handler runs.
 */
const MountedFieldsProbe = ({ mountedFields }: { mountedFields: MutableRefObject<MountedFieldsReader> }) => {
  const { control } = useFormContext();

  useEffect(() => {
    mountedFields.current = () => control._names.mount;
  }, [control, mountedFields]);

  return null;
};

const SoftwareReleaseEditToolbar = ({ disabled, ...props }: ToolbarProps & { disabled?: boolean }) => (
  <Toolbar {...props}>
    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton disabled={disabled} />
      <DeleteButton />
    </Box>
  </Toolbar>
);

const SoftwareReleaseEditForm = () => {
  const record = useRecordContext<SoftwareRelease>();
  const { save } = useSaveContext();
  const translate = useTranslate();

  /**
   * The hardware the form currently describes. Kept here rather than watched inside the form
   * because the component list, the save button and the payload must all agree on one hardware.
   * It is tracked per record id so that editing another release does not inherit this one's pick.
   */
  const [picked, setPicked] = useState<{ recordId?: Identifier; hardwareId?: number }>({});
  const hardwareId = picked.recordId === record?.id ? picked.hardwareId : record?.hardware_id;

  const [formErrors, setFormErrors] = useState<ValidationErrorMessage[]>([]);
  const mountedFields = useRef<MountedFieldsReader>(undefined);

  const {
    data: components,
    isLoading,
    error,
  } = useGetList<SoftwareComponent>(
    'software_components',
    { filter: { hardware_id: hardwareId } },
    { enabled: hardwareId != null },
  );

  /** An untouched edit must round-trip the record's own selection, component for component. */
  const seededVersions = useMemo(() => seedComponentVersions(record), [record]);
  const defaultValues = useMemo(() => ({ [COMPONENT_VERSIONS]: seededVersions }), [seededVersions]);
  const clearedVersions = useMemo(
    () => Object.fromEntries(Object.keys(seededVersions).map((key) => [key, null])),
    [seededVersions],
  );

  const handleHardwareChange = useCallback(
    (nextHardwareId?: number) => {
      setPicked({ recordId: record?.id, hardwareId: nextHardwareId });
      setFormErrors([]);
    },
    [record?.id],
  );

  const componentsUnavailable = hardwareId == null || isLoading || error != null;

  /**
   * Saving by hand rather than through the toolbar's `transform`.
   *
   * TVR-1915 moved the Laravel-shape translation into the data provider, so the *field-bound* half
   * of this would now work without any code here. The local handling stays for the other half:
   * `software_version_ids` is validated by the API but bound to no input on this form, and only a
   * form knows which of its fields actually have a mounted input. A provider cannot — it never sees
   * the JSX — so partitioning bound from unbound errors, and the alert that shows the unbound ones,
   * necessarily stay here. Deliberately not migrated; see the ticket.
   */
  const handleSubmit = useCallback(
    async (values: FieldValues) => {
      if (save == null) {
        return undefined;
      }

      if (componentsUnavailable) {
        setFormErrors(['resources.software_releases.edit.components_error']);
        return undefined;
      }

      setFormErrors([]);
      const result: unknown = await save(buildPayload(values, components ?? [], record));
      if (result == null) {
        return undefined;
      }

      const { fieldErrors, formErrors: unboundErrors } = partitionServerErrors(
        result,
        (field) => mountedFields.current?.().has(field) ?? false,
      );
      setFormErrors(unboundErrors);

      return fieldErrors;
    },
    [components, componentsUnavailable, record, save],
  );

  return (
    <SimpleForm
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      toolbar={<SoftwareReleaseEditToolbar disabled={componentsUnavailable} />}
    >
      <MountedFieldsProbe mountedFields={mountedFields} />
      {formErrors.length > 0 && (
        <Alert severity="error" sx={{ width: '100%' }}>
          <AlertTitle>{translate('resources.software_releases.edit.save_failed')}</AlertTitle>
          {formErrors.map((error, index) => (
            // eslint-disable-next-line react/no-array-index-key -- two fields can report the same message
            <div key={index}>
              <ValidationError error={error} />
            </div>
          ))}
        </Alert>
      )}
      <TextInput source="name" placeholder="Pixie v17.1.0" fullWidth validate={required()} />
      <HardwareInput
        hardwareId={hardwareId}
        seededVersions={seededVersions}
        clearedVersions={clearedVersions}
        onHardwareChange={handleHardwareChange}
      />
      <FormDataConsumer>
        {({ formData }) =>
          formData.hardware_id && (
            <ReferenceInput
              source="hardware_revision_id"
              reference="hardware_revisions"
              filter={{ hardware_id: formData.hardware_id }}
            >
              <SelectInput optionText="name" fullWidth validate={required()} />
            </ReferenceInput>
          )
        }
      </FormDataConsumer>
      <BooleanInput source="custom" />
      <Typography variant="h5">{translate('resources.software_releases.fields.software_components')}</Typography>
      <SoftwareComponentInputs
        components={components}
        hardwareId={hardwareId}
        isLoading={isLoading}
        hasError={error != null}
        enforceRequired={releaseHasVersions(record)}
      />
    </SimpleForm>
  );
};

export const SoftwareReleaseEdit: FC<EditProps> = () => {
  return (
    <Edit mutationMode="pessimistic">
      <SoftwareReleaseEditForm />
    </Edit>
  );
};
